import React, { useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Tooltip from '@mui/material/Tooltip';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { useRanDbDashboard } from './context/ranDbDashboardContext';
import {
  allSitesRanDb,
  openAddInfoFormRanDb,
  selectedRanDbRow,
  ranDbViewMode,
  ranDbArchivedType,
  openDeleteSiteModal,
  selectedSiteForDeletion,
  openUploadFileRanDb,
} from '../../common/atom/atoms';
import edit from '../../Assets/AlertIcon/edits.svg';
import {
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Chip } from '@mui/material';
import { Dialog, DialogTitle, DialogActions, Button, Box } from '@mui/material';
import ReportAnalyticsIcon from '../../Assets/AlertIcon/report-analytics.svg';
import DownloadIconButton from '../../common/components/DownloadIconButton';
import GenerateReportRanDB from './generate_report_ran_db.component';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import RanDbSiteDetailsModal from './RanDbSiteDetailsModal';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { AuditLogsPopup } from './audit_logs_popup.component';
import PaginationComponent from '../../common/components/PaginationComponent';
import FancyLoader from '../../common/components/FancyLoader.component';
import { notify } from '../../common/functions/react-toastify.function';
import ColumnSelectionModal from '../fiberSitePlanning/components/ColumnSelectionModal';
import { convertDateToLocalString } from '../../common/functions/convert_date_to_local_string.function';

const ARCHIVED_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Dismantle', value: 'Dismantle' },
  { label: 'Relocation', value: 'Relocation' },
  { label: 'Other', value: 'Other' },
];

export default function RanDBTable({
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  selectedStatus = 'All',
  onStatusChange,
  showPagination = true,
  searchQuery = '',
  onSearch,
  onClearSearch,
  isSearching = false,
  onSort,
  sortBy = '',
  sortOrder = 'ASC',
}) {
  const data = useAtomValue(allSitesRanDb);
  const validData = Array?.isArray(data) ? data : [];
  const { dashboardData } = useRanDbDashboard();
  const setOpenAddInfoForm = useSetAtom(openAddInfoFormRanDb);
  const setSelectedRow = useSetAtom(selectedRanDbRow);
  const setOpenUploadFile = useSetAtom(openUploadFileRanDb);
  const viewMode = useAtomValue(ranDbViewMode);
  const isArchivedMode = viewMode === 'archived';

  console.log('chk', isArchivedMode);
  const [archivedType, setArchivedType] = useAtom(ranDbArchivedType);
  const [openReportDialog, setOpenReportDialog] = React.useState(false);

  // Menu state for three-dot menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedSiteForDetails, setSelectedSiteForDetails] = useState(null);
  const [lockFilter, setLockFilter] = useState('All');
  const [fiberStatusFilter, setFiberStatusFilter] = useState('All');

  // Upload menu state
  const [uploadAnchorEl, setUploadAnchorEl] = useState(null);
  const uploadMenuOpen = Boolean(uploadAnchorEl);

  // Audit logs state
  const [auditLogsOpen, setAuditLogsOpen] = useState(false);

  // Search state
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);
  const [openColumnSelectionModal, setOpenColumnSelectionModal] = useState(false);

  // Universal sorting function that works for all columns
  const handleSortingChange = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue([]) : updaterOrValue;

    if (newSorting && newSorting.length > 0) {
      const sort = newSorting[0];
      const field = sort.id;
      const direction = sort.desc ? 'DESC' : 'ASC';

      if (onSort) {
        onSort(field, direction);
      }
    } else {
      // Clear sorting
      if (onSort) {
        onSort('', 'ASC');
      }
    }
  };

  // Handle search input changes
  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setLocalSearchQuery(value);
  }, []);

  // Debounced search effect - only trigger when localSearchQuery changes
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch && localSearchQuery.trim().length > 1) {
        // Only search if site code has more than 1 character
        onSearch(localSearchQuery);
      } else if (onSearch && localSearchQuery.trim().length <= 1) {
        // Clear search if site code is 1 character or less
        if (isSearching) {
          onClearSearch();
        }
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localSearchQuery]); // Only depend on localSearchQuery

  // Handle search clear
  const handleSearchClear = React.useCallback(() => {
    setLocalSearchQuery('');
    if (onClearSearch) {
      onClearSearch();
    }
  }, [onClearSearch]);

  // Update local search query when prop changes
  React.useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Define which columns should be visible by default - only essential columns
  const defaultColumnVisibility = {
    site_code: true,
    physical: true,
    locked_status: true,
    vendor: true,
    region: true,
    coverage_type: true,
    // All other columns are hidden by default
    lat: false,
    long: false,
    site_status: true,
    pta_tehsil: false,
    pta_district: false,
    pta_province: false,
    prefix: false,
    archived_type: viewMode === 'detailed' ? false : viewMode === 'archived' ? true : false,
    city_prefix_based: false,
    technical_city: false,
    commercial_district: false,
    commercial_city: false,
    mbu: false,
    franchise_id: false,
    site_type: false,
    sharing_type_hostguest: false,
    omo_name: false,
    b2s_id: false,
    bsc: false,
    bsc_code: false,
    lte_technology: false,
    deodar_tag: false,
    uploaded_at: true,
  };

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState(() => ({
    ...defaultColumnVisibility,
    archived_type: viewMode === 'detailed' ? false : viewMode === 'archived' ? true : false,
  }));

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = {
      'On-Air': 0,
      'In-Progress': 0,
      Planned: 0,
      'Non-Fiber': 0,
      Descoped: 0,
      Total: validData.length,
    };

    let fiberStatusCount = 0; // Count all sites with any status

    validData.forEach((item) => {
      const status = item.status;
      if (counts.hasOwnProperty(status)) {
        counts[status]++;
      }
      // Count all sites that have any non-null status
      if (status && status.trim() !== '') {
        fiberStatusCount++;
      }
    });

    return {
      ...counts,
      FiberStatus: fiberStatusCount, // Add fiber status count
    };
  }, [validData]);

  // Filter data based on lock status and fiber status
  const filteredData = useMemo(() => {
    return validData.filter((item) => {
      // Lock status filter
      const lockedStatus = item.locked_status;
      let lockStatusMatch = true;
      if (lockFilter !== 'All') {
        if (lockFilter === 'Locked') {
          lockStatusMatch = lockedStatus === 'Locked';
        } else if (lockFilter === 'Temporary Locked') {
          lockStatusMatch = lockedStatus === 'Temporary Locked';
        }
      }

      // Fiber status filter
      const fiberStatus = item.status;
      let fiberStatusMatch = true;
      if (fiberStatusFilter !== 'All') {
        fiberStatusMatch = fiberStatus === fiberStatusFilter;
      }

      return lockStatusMatch && fiberStatusMatch;
    });
  }, [validData, lockFilter, fiberStatusFilter]);

  // Delete modal state
  const setOpenDeleteModal = useSetAtom(openDeleteSiteModal);
  const setSelectedSiteForDeletion = useSetAtom(selectedSiteForDeletion);

  const handleArchivedTabClick = (value) => {
    setArchivedType(value);
  };

  const handleOpenMenu = (event, rowData) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowForMenu(rowData);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleViewSite = () => {
    setDetailsModalOpen(true);
    setSelectedSiteForDetails(selectedRowForMenu);
    handleCloseMenu();
  };

  const handleEditSite = () => {
    setSelectedRow(selectedRowForMenu);
    setOpenAddInfoForm(true);
    handleCloseMenu();
  };

  const handleDeleteSite = () => {
    setSelectedSiteForDeletion(selectedRowForMenu);
    setOpenDeleteModal(true);
    handleCloseMenu();
  };

  // Upload menu handlers
  const handleUploadClick = (event) => {
    setUploadAnchorEl(event.currentTarget);
  };

  const handleUploadClose = () => {
    setUploadAnchorEl(null);
  };

  const handleAddSingleFile = () => {
    setOpenAddInfoForm(true);
    handleUploadClose();
  };

  const handleUploadFile = () => {
    setOpenUploadFile(true);
    handleUploadClose();
  };

  // Audit logs handler
  const handleAuditLogsClick = () => {
    setAuditLogsOpen(true);
  };

  const columns = useMemo(() => {
    // Define only the 27 specific fields for RAN DB
    const allColumns = [
      // Site Code column with custom styling
      {
        accessorKey: 'site_code',
        header: 'Site Code',
        size: 120,
        muiTableHeadCellProps: {
          style: { paddingLeft: '24px' },
        },
        Cell: ({ cell }) => <div style={{ paddingLeft: '24px' }}>{cell.getValue()}</div>,
      },

      // Physical

      // Vendor
      { accessorKey: 'vendor', header: 'Vendor', size: 120 },

      // Region
      { accessorKey: 'region', header: 'Region', size: 120 },

      // Coverage Type
      { accessorKey: 'coverage_type', header: 'Coverage Type', size: 120 },

      // Latitude
      { accessorKey: 'lat', header: 'Latitude', size: 120 },

      // Longitude
      { accessorKey: 'long', header: 'Longitude', size: 120 },

      // Site Status
      { accessorKey: 'physical', header: 'Physical', size: 120 },
      { accessorKey: 'site_status', header: 'Site Status', size: 120 },

      {
        accessorKey: 'status',
        header: 'Fiber Status',
        size: 100,
        Cell: ({ row }) => {
          const status = row.original.status;
          const getStatusColor = (status) => {
            switch (status) {
              case 'On-Air':
                return { label: 'On Air', color: '#12A150', bg: '#E8FAF0' };
              case 'In-Progress':
                return { label: 'In Progress', color: '#2196F3', bg: '#E5F6FD' };
              case 'Planned':
                return { label: 'Planned', color: '#BD1D23', bg: '#F8E8E9' };
              case 'Non-Fiber':
                return { label: 'Non Fiber', color: '#FF9800', bg: '#FFF3E0' };
              case 'Descoped':
                return { label: 'Descoped', color: '#9C27B0', bg: '#F3E5F5' };
              default:
                return { label: status, color: '#9E9E9E', bg: 'rgba(158, 158, 158, 0.1)' };
            }
          };
          const { label, color, bg } = getStatusColor(status);
          return status ? (
            <Chip
              label={label}
              size="small"
              style={{ color, backgroundColor: bg, fontWeight: 500, borderRadius: -1 }}
            />
          ) : null;
        },
      },

      // Locked Status with custom cell

      {
        accessorKey: 'locked_status',
        header: 'Status',
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.locked_status;
          const getStatusColor = (status) => {
            switch (status) {
              case 'Temporary Locked':
                return {
                  label: 'Temporary Locked',
                  color: '#2196F3',
                  bg: '#E5F6FD',
                };
              case 'Inactive':
                return { label: 'Inactive', color: '#12A150', bg: '#E8FAF0' };
              case 'Locked':
                return { label: 'Locked', color: '#BD1D23', bg: '#F8E8E9' };
              default:
                return { label: status, color: '#9E9E9E', bg: 'rgba(158, 158, 158, 0.1)' };
            }
          };
          const { label, color, bg } = getStatusColor(status);
          return status ? (
            <Chip
              label={label}
              size="small"
              style={{
                color,
                backgroundColor: bg,
                fontWeight: 500,
                borderRadius: -1,
              }}
            />
          ) : null;
        },
      },
      {
        accessorKey: 'archived_type',
        header: 'Archived Type',
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.archived_type;
          const getStatusColor = (status) => {
            switch (status) {
              case 'Dismantled':
                return {
                  label: 'Dismantled',
                  color: '#2196F3',
                  bg: '#E5F6FD',
                };
              case 'Relocation':
                return { label: 'Relocation', color: '#12A150', bg: '#E8FAF0' };
              case 'Locked':
                return { label: 'Locked', color: '#BD1D23', bg: '#F8E8E9' };
              default:
                return { label: status, color: '#9E9E9E', bg: 'rgba(158, 158, 158, 0.1)' };
            }
          };
          const { label, color, bg } = getStatusColor(status);
          return status ? (
            <Chip
              label={label}
              size="small"
              style={{
                color,
                backgroundColor: bg,
                fontWeight: 500,
                borderRadius: -1,
              }}
            />
          ) : null;
        },
      },

      // PTA Tehsil
      { accessorKey: 'pta_tehsil', header: 'PTA Tehsil', size: 120 },

      // PTA District
      { accessorKey: 'pta_district', header: 'PTA District', size: 120 },

      // PTA Province
      { accessorKey: 'pta_province', header: 'PTA Province', size: 120 },

      // Prefix
      { accessorKey: 'prefix', header: 'Prefix', size: 120 },

      // City Prefix Based
      { accessorKey: 'city_prefix_based', header: 'City Prefix', size: 120 },

      // Technical City
      { accessorKey: 'technical_city', header: 'Technical City', size: 120 },

      // Commercial District
      { accessorKey: 'commercial_district', header: 'Commercial District', size: 140 },

      // Commercial City
      { accessorKey: 'commercial_city', header: 'Commercial City', size: 140 },

      // MBU
      { accessorKey: 'mbu', header: 'MBU', size: 120 },

      // Franchise ID
      { accessorKey: 'franchise_id', header: 'Franchise ID', size: 120 },

      // Site Type
      { accessorKey: 'site_type', header: 'Site Type', size: 120 },

      // Sharing Type Host/Guest
      { accessorKey: 'sharing_type_hostguest', header: 'Sharing Type', size: 120 },

      // OMO Name
      { accessorKey: 'omo_name', header: 'OMO Name', size: 120 },

      // B2S ID
      { accessorKey: 'b2s_id', header: 'B2S ID', size: 120 },

      // BSC
      { accessorKey: 'bsc', header: 'BSC', size: 120 },

      // BSC Code
      { accessorKey: 'bsc_code', header: 'BSC Code', size: 120 },

      // LTE Technology
      { accessorKey: 'lte_technology', header: 'LTE Technology', size: 120 },

      // Deodar Tag
      { accessorKey: 'deodar_tag', header: 'Deodar Tag', size: 120 },

      // Uploaded At
      {
        accessorKey: 'uploaded_at',
        header: 'Uploaded At',
        size: 160,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? convertDateToLocalString(value) : '--';
        },
      },
    ];

    return allColumns;
  }, []);

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableColumnVisibility: true,
    state: {
      columnVisibility,
      sorting: sortBy ? [{ id: sortBy, desc: sortOrder === 'DESC' }] : [],
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: handleSortingChange,
    enableDensityToggle: true,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableSorting: true,
    enableFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableRowActions: true,
    enableRowDragging: false,
    enableColumnDragging: false,
    enablePinning: true,
    enableRowVirtualization: false,
    enableColumnVirtualization: false,
    manualSorting: true, // Enable backend sorting
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={(e) => handleOpenMenu(e, row.original)}
        size="small"
        sx={{
          padding: '2px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '', // Hide the header text for actions column
      },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      // sx: {
      //   '& .MuiBox-root': {
      //     display: 'contents',
      //   },
      // },
    },
    // muiPaginationProps: {
    //   color: 'secondary',
    //   rowsPerPageOptions: [10, 25, 50, 100],
    //   shape: 'square', // Square boxes
    //   variant: 'outlined',
    //   sx: {
    //     '& .MuiPaginationItem-root.Mui-selected': {
    //       backgroundColor: '#BD1D23',
    //       color: 'white',
    //     },
    //   },
    // },
    enableBottomToolbar: false,
    enableStickyHeader: true,
    enableStickyFooter: false, // Remove empty footer since pagination is disabled
    enablePagination: false,
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 275px)',
        overflowX: 'auto',
        width: '100%',
        marginBottom: 0, // Remove bottom margin
        paddingBottom: 0, // Remove bottom padding
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'auto', // Changed from 'fixed' to 'auto' for better action column positioning
          marginBottom: 0, // Remove table bottom margin
        },
        '& .MuiTableCell-root': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px 8px',
          textAlign: 'center',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
        // Remove any extra spacing from Material-UI table
        '& .MuiTablePagination-root': {
          marginTop: 0,
          paddingTop: 0,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: 'transparent', // Changed from '#fff' to 'transparent' to inherit row color
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '20px', // Further reduced row height for compact density
        padding: '1px 4px', // Further reduced padding for compact density
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: (() => {
          const fiberStatus = row.original.status; // This is the Fiber Status column
          if (fiberStatus === 'On-Air') {
            return '#f0fff0'; // Very light green
          } else if (fiberStatus === 'Planned') {
            return '#fff8f0'; // Very light orange
          } else if (fiberStatus === 'In-Progress') {
            return '#f0f8ff'; // Very light blue
          } else if (fiberStatus === 'Non-Fiber') {
            return '#fff8e0'; // Very light orange for Non-Fiber
          } else if (fiberStatus === 'Descoped') {
            return '#f8f0ff'; // Very light purple for Descoped
          }
          return '#fff'; // Default white
        })(),
        '&:hover': {
          backgroundColor: (() => {
            const fiberStatus = row.original.status; // This is the Fiber Status column
            if (fiberStatus === 'On-Air') {
              return '#e6ffe6'; // Slightly darker green on hover
            } else if (fiberStatus === 'Planned') {
              return '#ffe6cc'; // Slightly darker orange on hover
            } else if (fiberStatus === 'In-Progress') {
              return '#e6f3ff'; // Slightly darker blue on hover
            } else if (fiberStatus === 'Non-Fiber') {
              return '#ffe6cc'; // Slightly darker orange on hover for Non-Fiber
            } else if (fiberStatus === 'Descoped') {
              return '#f0e6ff'; // Slightly darker purple on hover for Descoped
            }
            return '#f5f5f5'; // Default hover color
          })(),
        },
      },
    }),
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '12px', // Reduced font size
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '32px', // Reduced header height
        padding: '4px 8px', // Reduced header padding
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        width: '100%',
        overflow: 'hidden',
        '& .MuiTableContainer-root': {
          overflowX: 'auto',
          maxWidth: '100%',
        },
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'auto', // Changed from 'fixed' to 'auto' for better action column positioning
        },
        '& .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px',
          textAlign: 'center',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px 8px',
          textAlign: 'center',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        style={{
          display: '-webkit-inline-box',
          alignItems: 'center',
          width: '66%',
          flexWrap: 'nowrap',
          minWidth: 0,
          gap: 8,
        }}
      >
        {/* Search Input */}
        <TextField
          size="small"
          placeholder="Search all..."
          value={localSearchQuery}
          onChange={handleSearchChange}
          variant="outlined"
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              height: '32px',
              fontSize: '12px',
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#BD1D23',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#BD1D23',
              },
            },
            '& .MuiInputBase-input': {
              padding: '6px 12px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: '#666' }} />
              </InputAdornment>
            ),
            endAdornment: localSearchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearchClear} sx={{ padding: '2px' }}>
                  <ClearIcon sx={{ fontSize: 16, color: '#666' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {/* Upload Button with Gradient */}
        {!isArchivedMode && (
          <Button
            variant="contained"
            size="small"
            style={{
              background: '#BD1D23',
              color: 'white',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              padding: '6px 16px',
              fontSize: '12px',
              boxShadow: '0 2px 8px rgba(245, 248, 247, 0.3)',
              border: 'none',
              textTransform: 'none',
            }}
            endIcon={<ArrowDropDownIcon style={{ fontSize: 16, color: 'white' }} />}
            onClick={handleUploadClick}
          >
            Upload
          </Button>
        )}

        {/* Lock Filter Dropdown - Only show for Detailed mode */}
        {!isArchivedMode && (
          <FormControl size="small" sx={{ minWidth: 120 }} variant="outlined">
            <InputLabel
              id="lock-filter-label"
              sx={{
                color: '#333',
                fontSize: '12px',
                fontWeight: 500,
                '&.Mui-focused': {
                  color: '#333',
                },
                '&.MuiInputLabel-shrink': {
                  color: '#333',
                  fontSize: '11px',
                },
              }}
            >
              Lock Status
            </InputLabel>

            <Select
              labelId="lock-filter-label"
              id="lock-filter"
              value={lockFilter}
              label="Lock Status" // This is REQUIRED to float label correctly
              onChange={(e) => setLockFilter(e.target.value)}
              sx={{
                background: '#f8e8e9',
                color: '#333',
                fontWeight: 600,
                fontSize: '12px',
                height: '32px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '& .MuiSelect-icon': {
                  color: '#333',
                },
                '& .MuiSelect-select': {
                  padding: '6px 12px',
                  lineHeight: 1.2,
                  color: '#333',
                  fontWeight: 600,
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                  fontSize: '12px',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#333',
                },
                '& .MuiInputLabel-shrink': {
                  color: '#333',
                  fontSize: '11px',
                },
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '12px' }}>
                All
              </MenuItem>
              <MenuItem value="Locked" sx={{ fontSize: '12px' }}>
                Locked
              </MenuItem>
              <MenuItem value="Temporary Locked" sx={{ fontSize: '12px' }}>
                Temporary Locked
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Fiber Status Filter Dropdown - Only show for Detailed mode */}
        {!isArchivedMode && (
          <FormControl size="small" sx={{ minWidth: 120, ml: 1 }} variant="outlined">
            <InputLabel
              id="fiber-status-filter-label"
              sx={{
                color: '#333',
                fontSize: '12px',
                fontWeight: 500,
                '&.Mui-focused': {
                  color: '#333',
                },
                '&.MuiInputLabel-shrink': {
                  color: '#333',
                  fontSize: '11px',
                },
              }}
            >
              Fiber Status
            </InputLabel>

            <Select
              labelId="fiber-status-filter-label"
              id="fiber-status-filter"
              value={selectedStatus}
              label="Fiber Status" // This is REQUIRED to float label correctly
              onChange={(e) => onStatusChange(e.target.value)}
              sx={{
                background: '#f8e8e9',
                color: '#333',
                fontWeight: 600,
                fontSize: '12px',
                height: '32px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
                '& .MuiSelect-icon': {
                  color: '#333',
                },
                '& .MuiSelect-select': {
                  padding: '6px 12px',
                  lineHeight: 1.2,
                  color: '#333',
                  fontWeight: 600,
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                  fontSize: '12px',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#333',
                },
                '& .MuiInputLabel-shrink': {
                  color: '#333',
                  fontSize: '11px',
                },
              }}
            >
              <MenuItem value="All" sx={{ fontSize: '12px' }}>
                All
              </MenuItem>
              <MenuItem value="On-Air" sx={{ fontSize: '12px' }}>
                On Air
              </MenuItem>
              <MenuItem value="In-Progress" sx={{ fontSize: '12px' }}>
                In Progress
              </MenuItem>
              <MenuItem value="Planned" sx={{ fontSize: '12px' }}>
                Planned
              </MenuItem>
              <MenuItem value="Non-Fiber" sx={{ fontSize: '12px' }}>
                Non Fiber
              </MenuItem>
              <MenuItem value="Descoped" sx={{ fontSize: '12px' }}>
                Descoped
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Audit Logs Button with Gradient */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#f8e8e9',
            color: '#333',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
            border: 'none',
            textTransform: 'none',
          }}
          onClick={handleAuditLogsClick}
        >
          <ReportGmailerrorredIcon style={{ fontSize: 16, marginRight: 4, color: '#333' }} />
          Audit
        </Button>

        {!isArchivedMode && (
          <Button
            variant="contained"
            size="small"
            style={{
              background: '#f8e8e9',
              color: '#333',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              padding: '6px 16px',
              fontSize: '12px',
              boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
              border: 'none',
              textTransform: 'none',
            }}
            onClick={() => setOpenReportDialog(true)}
          >
            <img
              src={ReportAnalyticsIcon}
              alt="Report Analytics"
              style={{ width: 16, height: 16, marginRight: 4 }}
            />
            GENERATE REPORT
          </Button>
        )}

        {/* Download Button with Dropdown */}
        <Button
          variant="contained"
          size="small"
          disabled={isDownloading}
          style={{
            background: '#f8e8e9',
            color: '#333',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
            border: 'none',
            textTransform: 'none',
            opacity: isDownloading ? 0.7 : 1,
          }}
          onClick={handleDownloadClick}
          endIcon={<ArrowDropDownIcon style={{ fontSize: 16, color: '#333' }} />}
        >
          {isDownloading ? (
            <>
              <CircularProgress size={16} style={{ marginRight: 4, color: '#333' }} />
              Downloading...
            </>
          ) : (
            <>
              <DownloadIcon style={{ fontSize: 16, marginRight: 4, color: '#333' }} />
              Download
            </>
          )}
        </Button>

        {/* Status Counter Tags */}
        {/* <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
       
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '6px',
              backgroundColor: '#F5F5F5',
              border: '1px solid #666',
              color: '#666',
              fontSize: '11px',
              fontWeight: 600,
              height: '32px',
              minHeight: '32px',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.5s',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(102, 102, 102, 0.3)',
                borderColor: '#555',
                '&::before': {
                  left: '100%',
                },
                '& .status-dot': {
                  transform: 'scale(1.2)',
                  boxShadow: '0 0 8px rgba(102, 102, 102, 0.6)',
                },
              },
              animation: 'fadeInUp 0.6s ease-out 0.5s both',
            }}
          >
            <Box
              className="status-dot"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#666',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(102, 102, 102, 0.3)',
                  transform: 'translate(-50%, -50%) scale(0)',
                  animation: 'pulse 2s infinite 2.5s',
                },
              }}
            />
            Total {statusCounts['Total']}
          </Box>
        </Box> */}
      </div>
    ),
  });

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const downloadFile = async () => {
    // Check if data is available
    if (!dashboardData || dashboardData.length === 0) {
      notify('warning', 'No data available to download. Please wait for data to load.');
      handleDownloadClose();
      return;
    }

    try {
      setIsDownloading(true);
      // Use all dashboard data instead of filtered table data
      const dataToDownload = dashboardData || [];
      downloadExcelSheetFunction(dataToDownload, 'randb');
    } catch (error) {
      console.error('Error downloading file:', error);
      notify('error', 'Failed to download file. Please try again.');
    } finally {
      setIsDownloading(false);
      handleDownloadClose();
    }
  };

  const handleDownloadColumnWise = () => {
    handleDownloadClose();
    setOpenColumnSelectionModal(true);
  };

  const handleDownloadSelectedColumns = (selectedColumnKeys) => {
    // Use the actual table data (validData) which includes all fields from the API
    const dataToDownload = dashboardData || [];

    if (dataToDownload.length === 0) {
      notify('warning', 'No data found to download.');
      return;
    }

    // Create a mapping of accessorKey to header for better column names in Excel
    const columnMap = {};
    columns.forEach((col) => {
      columnMap[col.accessorKey] = col.header;
    });

    // Prepare data with only selected columns
    const filteredData = dataToDownload.map((item) => {
      const filteredItem = {};
      selectedColumnKeys.forEach((key) => {
        // Format uploaded_at date if it's selected
        if (key === 'uploaded_at' && item[key]) {
          filteredItem[columnMap[key] || key] = convertDateToLocalString(item[key]);
        } else {
          filteredItem[columnMap[key] || key] = item[key] || '';
        }
      });
      return filteredItem;
    });

    // Download using the existing function
    downloadExcelSheetFunction(filteredData, 'randb');
    notify('success', `Downloaded ${selectedColumnKeys.length} columns successfully.`);
  };

  return (
    <div style={{ marginLeft: '32px', marginRight: '32px', width: 'calc(100% - 64px)' }}>
      <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            marginBottom: '8px',
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Loading overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                // backgroundColor: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '8px',
                // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <FancyLoader message="Loading..." fullScreen={false} size="small" />
            </Box>
          )}
          {isArchivedMode && (
            <>
              <Box sx={{ padding: '16px 25px', backgroundColor: '#fff' }}>
                {/* TABS SECTION ABOVE TABLE */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    borderBottom: '1px solid #ccc',
                    width: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  {ARCHIVED_TABS.map((tab, index) => {
                    const key = tab.value.toUpperCase().replace(' ', '_');
                    const isSelected = archivedType === tab.value;

                    return (
                      <Box
                        key={key}
                        onClick={() => handleArchivedTabClick(tab.value)}
                        sx={{
                          cursor: 'pointer',
                          px: 2,
                          py: 1,
                          borderBottom: isSelected ? '2px solid #BD1D23' : '2px solid transparent',
                          fontWeight: isSelected ? 700 : 400,
                          fontSize: isSelected ? '15px' : '14px',
                          color: '#000',
                        }}
                      >
                        {tab.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
          <div
            className="box"
            style={{
              width: '100%',
              overflow: 'hidden',
              padding: '0px',
              margin: '0px',
            }}
          >
            <MaterialReactTable table={table} />
          </div>
        </Box>
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleViewSite}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View
        </MenuItem>
        {!isArchivedMode && (
          <MenuItem onClick={handleEditSite}>
            <img src={edit} style={{ width: 20, height: 20, marginRight: 8 }} />
            Edit
          </MenuItem>
        )}
        {!isArchivedMode && (
          <MenuItem onClick={handleDeleteSite}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Upload Menu */}
      <Menu
        anchorEl={uploadAnchorEl}
        open={uploadMenuOpen}
        onClose={handleUploadClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 150,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
          },
        }}
      >
        <MenuItem onClick={handleAddSingleFile}>Add Single Site</MenuItem>
        <MenuItem onClick={handleUploadFile}>Upload File</MenuItem>
      </Menu>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadAnchorEl}
        open={downloadMenuOpen}
        onClose={handleDownloadClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={downloadFile} disabled={isDownloading}>
          {isDownloading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          )}
          {isDownloading ? 'Downloading...' : 'Download All'}
        </MenuItem>
        <MenuItem onClick={handleDownloadColumnWise}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Download Columns
        </MenuItem>
      </Menu>

      {/* Column Selection Modal */}
      <ColumnSelectionModal
        open={openColumnSelectionModal}
        onClose={() => setOpenColumnSelectionModal(false)}
        columns={columns}
        onDownload={handleDownloadSelectedColumns}
        data={dashboardData}
      />
      <RanDbSiteDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        site={selectedSiteForDetails}
      />
      <GenerateReportRanDB open={openReportDialog} onClose={() => setOpenReportDialog(false)} />

      {/* Total Records Display - Show when pagination is hidden */}
      {!isArchivedMode && !showPagination && (
        <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {validData.length} entries
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {!isArchivedMode && showPagination && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          limit={pageSize}
          onPageChange={onPageChange}
          onLimitChange={onPageSizeChange}
        />
      )}

      {/* Audit Logs Popup */}
      <AuditLogsPopup
        open={auditLogsOpen}
        onClose={() => setAuditLogsOpen(false)}
        siteCode={null}
      />
    </div>
  );
}
